import { type SpringConfig, animated, useSpring } from '@react-spring/web'
import type { VariantProps } from 'class-variance-authority'
import { type MotionProps, type Variants, motion } from 'framer-motion'
import * as React from 'react'
import {
	bouncySpring,
	prefersReducedMotion,
	snappySpring,
} from '#app/utils/animations'
import { cn } from '#app/utils/misc'
import { Button, type buttonVariants } from './button'

interface AnimatedButtonProps
	extends React.ComponentProps<typeof Button>,
		VariantProps<typeof buttonVariants> {
	/**
	 * Enable entrance animation when component mounts
	 * @default false
	 */
	animateOnMount?: boolean
	/**
	 * Delay for entrance animation (in seconds)
	 * @default 0
	 */
	entranceDelay?: number
	/**
	 * Enable hover scale animation
	 * @default true
	 */
	enableHoverScale?: boolean
	/**
	 * Enable press/active scale animation
	 * @default true
	 */
	enablePressScale?: boolean
	/**
	 * Custom spring config for hover animation
	 */
	hoverSpringConfig?: SpringConfig
	/**
	 * Custom spring config for press animation
	 */
	pressSpringConfig?: SpringConfig
	/**
	 * Motion props to pass to the motion wrapper
	 */
	motionProps?: Omit<MotionProps, 'children' | 'style'>
}

/**
 * AnimatedButton - An enhanced Button component with smooth animations
 *
 * Features:
 * - Physics-based hover and press animations using React Spring
 * - Optional entrance animations using Framer Motion
 * - Respects prefers-reduced-motion
 * - Drop-in replacement for the base Button component
 * - Maintains all existing Button props and variants
 * - Default styles: `flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors`
 *   (can be overridden via className prop)
 *
 * @example
 * ```tsx
 * // Basic usage with default styles
 * <AnimatedButton variant="default" size="lg">
 *   Click me
 * </AnimatedButton>
 * ```
 *
 * @example
 * ```tsx
 * // Override default padding and add custom styles
 * <AnimatedButton
 *   variant="outline"
 *   animateOnMount
 *   className="px-12 py-5 rounded-full bg-black text-white"
 * >
 *   Custom Styled Button
 * </AnimatedButton>
 * ```
 */
export function AnimatedButton({
	animateOnMount = false,
	entranceDelay = 0,
	enableHoverScale = true,
	enablePressScale = true,
	hoverSpringConfig,
	pressSpringConfig,
	motionProps,
	className,
	children,
	...buttonProps
}: AnimatedButtonProps) {
	const [isHovered, setIsHovered] = React.useState(false)
	const [isPressed, setIsPressed] = React.useState(false)
	const shouldAnimate = !prefersReducedMotion()

	// Calculate target scale based on state
	const targetScale = React.useMemo(() => {
		if (!shouldAnimate) return 1
		if (enablePressScale && isPressed) return 0.98
		if (enableHoverScale && isHovered) return 1.02
		return 1
	}, [shouldAnimate, enablePressScale, enableHoverScale, isPressed, isHovered])

	// Use a single spring that responds to both hover and press states
	const scaleSpring = useSpring({
		scale: targetScale,
		config:
			isPressed && enablePressScale
				? (pressSpringConfig ?? bouncySpring)
				: (hoverSpringConfig ?? snappySpring),
		immediate: !shouldAnimate,
	})

	// Entrance animation variants
	const entranceVariants: Variants = {
		hidden: { opacity: 0, y: 10, scale: 0.95 },
		visible: {
			opacity: 1,
			y: 0,
			scale: 1,
			transition: {
				duration: 0.3,
				ease: [0.22, 1, 0.36, 1] as const,
				delay: entranceDelay,
			},
		},
	}

	const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
		setIsHovered(true)
		if (buttonProps.onMouseEnter) {
			buttonProps.onMouseEnter(
				e as unknown as React.MouseEvent<HTMLButtonElement>,
			)
		}
	}

	const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
		setIsHovered(false)
		setIsPressed(false)
		if (buttonProps.onMouseLeave) {
			buttonProps.onMouseLeave(
				e as unknown as React.MouseEvent<HTMLButtonElement>,
			)
		}
	}

	const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
		setIsPressed(true)
		if (buttonProps.onMouseDown) {
			buttonProps.onMouseDown(
				e as unknown as React.MouseEvent<HTMLButtonElement>,
			)
		}
	}

	const handleMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
		setIsPressed(false)
		if (buttonProps.onMouseUp) {
			buttonProps.onMouseUp(e as unknown as React.MouseEvent<HTMLButtonElement>)
		}
	}

	// Default styles that can be overridden by className
	// These provide consistent padding, font, and text styles across the app
	const defaultStyles =
		'flex items-center gap-2 px-6! py-5! text-sm font-normal transition-colors cursor-pointer'

	// If entrance animation is enabled, wrap with motion.div
	if (animateOnMount) {
		return (
			<motion.div
				initial={shouldAnimate ? 'hidden' : false}
				animate={shouldAnimate ? 'visible' : false}
				variants={shouldAnimate ? entranceVariants : undefined}
				{...motionProps}
			>
				<animated.div
					style={{
						transform: scaleSpring.scale.to((s) => `scale(${s})`),
						// display: 'inline-block',
					}}
					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
					onMouseDown={handleMouseDown}
					onMouseUp={handleMouseUp}
				>
					<Button
						{...buttonProps}
						className={cn(
							'transition-transform',
							defaultStyles,
							className, // User's className comes last to allow overrides
						)}
					>
						{children}
					</Button>
				</animated.div>
			</motion.div>
		)
	}

	// Without entrance animation, just use animated.div wrapper
	return (
		<animated.div
			style={{
				transform: scaleSpring.scale.to((s) => `scale(${s})`),
				// display: 'inline-block',
			}}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			onMouseDown={handleMouseDown}
			onMouseUp={handleMouseUp}
		>
			<Button
				{...buttonProps}
				className={cn(
					'transition-transform',
					defaultStyles,
					className, // User's className comes last to allow overrides
				)}
			>
				{children}
			</Button>
		</animated.div>
	)
}

export type { AnimatedButtonProps }
